import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect, useState } from 'react';
import { useCustomHotkeys } from '../../common/hooks/useCustomHotkeys';
import { S_Modal } from '../../design/modals/modal/S_Modal';
import { IRejectionReason } from '../modules/annotation/submodules/reviewRejectReasones/models/rejectionReason';
import { drawingScreenDisables, keyboardShortcutsServiceInstance } from '../services/keyboardShortcuts.service';
import { TextAreaWithAutocomplete } from './TextAreaWithAutocomplete';

export enum AnnotationConfirmationModalMode {
  SUBMIT = 'Submit',
  ACCEPT = 'Accept',
  SUBMITANDACCEPT = 'SubmitAndAccept',
  REJECT = 'Reject',
}

interface IAnnotationConfirmationModalProps {
  showAnnotationConfirmationModal: boolean;
  annotationConfirmationModalMode: AnnotationConfirmationModalMode;
  rejectAutoCompleteOptions: IRejectionReason[];
  onAnnotationConfirm(reason?: string): Promise<void>;
  onAnnotationCancel(): void;
}

export const AnnotationConfirmationModal = observer((props: IAnnotationConfirmationModalProps): ReactElement => {
  const [reason, setReason] = useState<string | undefined>(undefined);
  const { t } = useTranslator();

  useEffect(() => {
    if (props.showAnnotationConfirmationModal) {
      keyboardShortcutsServiceInstance.disableShortcuts(drawingScreenDisables);
    } else {
      keyboardShortcutsServiceInstance.enableShortcuts(drawingScreenDisables);
    }
  }, [props.showAnnotationConfirmationModal]);

  const getBody = (): ReactElement => {
    switch (props.annotationConfirmationModalMode) {
      case AnnotationConfirmationModalMode.ACCEPT:
        return <p>{t('accept_annotation_confirmation_body')}</p>;
      case AnnotationConfirmationModalMode.SUBMITANDACCEPT:
        return <p>{t('submit_and_accept_annotation_confirmation_body')}</p>;
      case AnnotationConfirmationModalMode.REJECT: {
        const autocompleteOptions = props.rejectAutoCompleteOptions.toSorted((a, b) => a.order - b.order).map((x) => x.reason);
        return (
          <>
            <div>
              <div>{t('reject_annotation_confirmation_body')} </div>
              <div>({t('enter_sharp_to_start_autocomplete')})</div>
            </div>
            <TextAreaWithAutocomplete
              maxLength={210}
              value={reason}
              onChange={handleReasonChange}
              autocompleteOptions={autocompleteOptions}
              autofocus
              counter
            />
          </>
        );
      }
      case AnnotationConfirmationModalMode.SUBMIT:
      default:
        return <p>{t('submit_annotation_confirmation_body')}</p>;
    }
  };

  const handleReasonChange = (value: string): void => {
    setReason(value);
  };

  const handleConfirm = (): void => {
    void props.onAnnotationConfirm(reason);
    setReason('');
  };

  const handleCancel = (): void => {
    setReason('');
    props.onAnnotationCancel();
  };

  useCustomHotkeys(
    'enter',
    () => {
      handleConfirm();
    },
    { enabled: props.showAnnotationConfirmationModal },
    [handleConfirm]
  );

  return (
    <div>
      <S_Modal
        isOpen={props.showAnnotationConfirmationModal}
        toggle={handleCancel}
        headerText={t(props.annotationConfirmationModalMode === AnnotationConfirmationModalMode.REJECT ? 'warning' : 'confirmation')}
        buttons={
          <>
            <Button
              color='neutral'
              variant='outlined'
              onClick={handleCancel}
            >
              {t('cancel')}
            </Button>
            <Button
              color='primary'
              onClick={handleConfirm}
            >
              {t(props.annotationConfirmationModalMode === AnnotationConfirmationModalMode.REJECT ? 'reject' : 'submit')}
            </Button>
          </>
        }
      >
        {getBody()}
      </S_Modal>
    </div>
  );
});
